// Google Analytics 4 (GA4) Utility with Consent Mode v2 & Strict Privacy (KVKK / GDPR)

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export const GA_TRACKING_ID = 'G-S2VYHS70XE';

// Ensure dataLayer & gtag are initialized safely
export const initGtag = () => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }
};

// Update Google Consent Mode v2 state
export const updateConsent = (analyticsGranted: boolean, marketingGranted: boolean = false) => {
  if (typeof window === 'undefined') return;
  initGtag();

  const consentState = {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: marketingGranted ? 'granted' : 'denied',
    ad_user_data: marketingGranted ? 'granted' : 'denied',
    ad_personalization: marketingGranted ? 'granted' : 'denied',
  };

  window.gtag('consent', 'update', consentState);

  // If granted and GTM/gtag script isn't loaded yet, load it asynchronously
  if (analyticsGranted && !document.getElementById('gtm-script')) {
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    document.head.appendChild(script);
  }
};

// Track Page Views across React Router transitions (No PII)
export const trackPageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: url,
      page_location: window.location.origin + url,
      page_title: title || document.title,
      send_to: GA_TRACKING_ID
    });
  }
};

// Whitelist of allowed non-PII params for custom conversion events
const ALLOWED_PARAM_KEYS = new Set([
  'event_category',
  'event_label',
  'value',
  'service_category',
  'form_location',
  'locale',
  'placement'
]);

// Track Custom Conversion & User Interaction Events with strict PII prevention
export const trackEvent = (
  action: string,
  params: {
    event_category?: string;
    event_label?: string;
    value?: number;
    [key: string]: any;
  } = {}
) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    const sanitizedParams: Record<string, any> = {
      send_to: GA_TRACKING_ID
    };

    // Filter out any potential PII keys (names, emails, phones, free messages, companies)
    for (const [key, value] of Object.entries(params)) {
      if (ALLOWED_PARAM_KEYS.has(key) && typeof value !== 'object') {
        sanitizedParams[key] = value;
      }
    }

    window.gtag('event', action, sanitizedParams);
  }
};
